import React, { useState } from "react";
import { Switch } from "@mui/material";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import MessageBlock from "components/shared/MessageBlock";
import { SmallBody } from "helpers/TypographyComponents";
import { useTranslation } from "react-i18next";
import "./ReinstateProvisionField.scss";
import StatusChip, { IStatusRule } from "../StatusChip";

type Hint = { label: string; value: string };

type Props = {
    affectedCount: number;
    value: string;
    onChange: (val: string) => void;
    hints?: Hint[];
    fetchError?: boolean;
    hideGroupInfo?: boolean;
    /** Open the reinstate section on mount when a stored % already exists. */
    defaultEnabled?: boolean;
};

const ReinstateProvisionField = ({
    affectedCount,
    value,
    onChange,
    hints,
    fetchError,
    hideGroupInfo,
    defaultEnabled = false,
}: Props) => {
    const [t] = useTranslation();
    const [enabled, setEnabled] = useState(defaultEnabled);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/,/g, ".");
        val = val.match(/(([0-9]*([.]))?[0-9]+)|[.]/g)?.join("") ?? "";
        if (val.length > 1 && val.startsWith("0") && val[1] !== ".") val = val.substring(1);
        if (+val > 100) return;
        onChange(val);
    };

    const handleToggle = (checked: boolean) => {
        setEnabled(checked);
        if (!checked) onChange("");
    };

    const isGroup = affectedCount > 1;

    const contractBadgeRules: Record<string, IStatusRule> = {
        concerned: {
            color: "purple",
            text: "",
        },
    };

    return (
        <div className={`reinstate-field ${enabled ? "reinstate-field--open" : ""}`}>
            <div className="reinstate-field__header">
                <div className="reinstate-field__header-text">
                    <span className={`reinstate-field__title ${enabled ? "reinstate-field__title--active" : ""}`}>
                        {t("Management.ReinstateProvision")}
                    </span>
                    <StatusChip
                        status="concerned"
                        statusRules={contractBadgeRules}
                        label={`${affectedCount} ${t("Management.ContractsConcerned", {
                            count: affectedCount,
                        })}`}
                    />
                </div>

                <Switch
                    checked={enabled}
                    onChange={(_, checked) => handleToggle(checked)}
                    size="small"
                    sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#004cac" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#004cac" },
                    }}
                />
            </div>

            <p className={`reinstate-field__desc ${enabled ? "reinstate-field__desc--active" : ""}`}>
                {t("Management.ReinstateProvisionDesc")}
            </p>

            {enabled && (
                <div className="reinstate-field__body">
                    <hr className="reinstate-field__divider" />

                    {fetchError ? (
                        <MessageBlock type="warning" small text={t("Management.ReinstateProvisionFetchError")} />
                    ) : (
                        <>
                            <div className="add-provision-input">
                                <div className="add-provision-input-label">
                                    <span>{t("Management.ReinstateRate")}</span>
                                </div>
                                <StyledTextField
                                    fullWidth
                                    value={value}
                                    onChange={handleChange}
                                    placeholder="0"
                                    InputProps={{
                                        endAdornment: (
                                            <span style={{ color: "#585858", marginLeft: 10, fontWeight: 500 }}>%</span>
                                        ),
                                    }}
                                />
                                {hints?.map((hint) => (
                                    <SmallBody key={hint.label} color="grey">
                                        {hint.label} {hint.value}
                                    </SmallBody>
                                ))}
                            </div>

                            {isGroup && value && !hideGroupInfo && (
                                <MessageBlock
                                    type="info"
                                    small
                                    text={t(
                                        affectedCount === 1
                                            ? "Management.ReinstateGroupInfoSingleProvision"
                                            : "Management.ReinstateGroupInfo",
                                        { count: affectedCount, percent: value || "–" },
                                    )}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReinstateProvisionField;
